import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';

const VerifyPageContainer = styled.div`
  max-width: 450px;
  margin: 5rem auto;
  padding: 2.5rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  text-align: center;
`;

const Title = styled.h1`
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: #666;
  margin-bottom: 2rem;
  word-break: break-all;
`;

const Button = styled.button`
  flex: 1;
  padding: 15px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  &:disabled {
    background-color: #ccc;
  }
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: #005eb8;
  text-decoration: underline;
  cursor: pointer;
  margin-top: 1.5rem;
  font-size: 0.9rem;
  &:disabled {
    color: #ccc;
  }
`;

const SpamNote = styled.p`
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: 12px 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1.2rem;
  text-align: center;
  letter-spacing: 0.5em;
`;

const VerifyLogin = () => {
  const { state } = useLocation();
  const email = state?.email;
  const fromLogin = state?.fromLogin; // Get the flag from the navigation state
  const navigate = useNavigate();
  const { requestLoginCode, login } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const requestCodeSent = useRef(false); // Prevent duplicate calls in React StrictMode

  useEffect(() => {
    // Only request a new code if the user came from the login page.
    // If they came from the payment success page, a code has already been sent.
    if (email && fromLogin) {
      // In development, React's StrictMode runs this effect twice.
      // This ref ensures the API call is only made once per page load.
      if (requestCodeSent.current) {
        return;
      }
      requestCodeSent.current = true;

      const handleRequestCode = async () => {
        setLoading(true);
        const result = await requestLoginCode(email);
        if (result.success) {
          toast.success(result.message);
        } else {
          // If the specific "not found" error occurs, navigate back with state.
          if (result.error === 'no email address found on our system, please try another one') {
            navigate('/login', { state: { error: 'email-not-found', emailForRetry: email } });
          } else {
            toast.error(result.error || 'Could not send login code.');
            navigate('/login'); // Go back for other errors
          }
        }
        setLoading(false);
      };
      handleRequestCode();
    } else if (!email) {
      // If there's no email at all, redirect to login.
      navigate('/login');
    }
  }, [email, fromLogin, navigate, requestLoginCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, code);
    if (result.success) {
      toast.success('Login successful!');
      navigate('/account');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const handleResendCode = async () => {
    setLoading(true);
    const result = await requestLoginCode(email);
    if (result.success) {
      toast.success('A new code has been sent. Please check your inbox and spam folder.');
    } else {
      toast.error(result.error || 'Could not resend login code.');
    }
    setLoading(false);
  };

  if (!email) return null;

  return (
    <VerifyPageContainer>
      <Title>Verify Your Identity</Title>
      <Subtitle>A login code has been sent to <strong>{email}</strong>.</Subtitle>
      <Subtitle>Please enter it below.</Subtitle>
      <Form onSubmit={handleSubmit}>
        <Input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="LOGIN-CODE" required />
        <Button type="submit" disabled={loading}>Sign In</Button>
      </Form>
      <ResendButton type="button" onClick={handleResendCode} disabled={loading}>
        Didn't receive a code? Click here to resend.
      </ResendButton>
      <SpamNote>Please check your spam folder if you can't find it!</SpamNote>
    </VerifyPageContainer>
  );
};

export default VerifyLogin;